import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Fade,
} from '@mui/material';
import { Save, Close } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from '../../../../../styles';
import { Modal } from '../../../../../components';
import { IApplication, IDropdownApplications, ApplicationDefaults } from '../../../../../models/smp';


interface AddEditApplicationModalProps {
  open: boolean;
  application?: IApplication;
  applicationTypes?: IDropdownApplications[];
  categories?: IDropdownApplications[];
  vendors?: IDropdownApplications[];
  onClose: () => void;
  onSave: (updatedApplication: IApplication) => void;
}

export const AddEditApplicationModal: FC<AddEditApplicationModalProps> = ({
  open,
  application,
  applicationTypes = [],
  categories = [],
  vendors = [],
  onClose,
  onSave,
}) => {
  const classes = useStyles();
  const [localApplication, setLocalApplication] = useState<IApplication>(
    application ? { ...application } : ApplicationDefaults.getDefault()
  );

  useEffect(() => {
    if (open) {
      setLocalApplication(application ? { ...application } : ApplicationDefaults.getDefault());
    }
  }, [open, application]);

  const handleChange = (field: keyof IApplication, value: any) => {
    setLocalApplication(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    const cleanedApplication = {
      ...localApplication,
      LogInCredentialsRequired: localApplication.LogInCredentialsRequired ?? false,
      UsageReportRequired: localApplication.UsageReportRequired ?? false,
      AdminOption: localApplication.AdminOption ?? false,
      SingleSignOn: localApplication.SingleSignOn ?? false,
      InvoiceToCorpPayables: localApplication.InvoiceToCorpPayables ?? false,
      FSPrepaid: localApplication.FSPrepaid ?? false,
      PublishInCatalog: localApplication.PublishInCatalog ?? false,
      Status: localApplication.Status || '',
      OwnerID: localApplication.OwnerID ?? null,
    };

    onSave(cleanedApplication);
  };

  const isSaveDisabled = !localApplication.name;

  return (
    <Modal
      maxWidth="sm"
      open={open}
      onClose={onClose}
      title={localApplication.applicationID ? 'Edit Application' : 'Add Application'}
    >
      <Fade in={open}>
        <Box component="form" autoComplete="off" className={classes.form}>
        <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                required
                label="Application Name"
                fullWidth
                variant="outlined"
                value={localApplication.name || ''}
                onChange={e => handleChange('name', e.target.value)}
              />
            </Grid>

            {/* Application Type Dropdown */}
            <Grid item xs={12}>
              <TextField
              select
              label="Application Type"
              fullWidth
              variant="outlined"
              value={localApplication.TypeID || ''}
              onChange={e => handleChange('TypeID', Number(e.target.value))}
            >
              {applicationTypes.map(type => (
                <MenuItem key={type.typeID} value={type.typeID}>
                  {type.typeName}
                </MenuItem>
              ))}
            </TextField>
            </Grid>

            {/* Category Dropdown */}
            <Grid item xs={12}>
              <TextField
                select
                label="Category"
                fullWidth
                variant="outlined"
                value={localApplication.CategoryID || ''}
                onChange={e => handleChange('CategoryID', Number(e.target.value))}
              >
                {categories.map(category => (
                  <MenuItem key={category.categoryID} value={category.categoryID}>
                    {category.categoryName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

          </Grid>
          <Grid container spacing={1} justifyContent="flex-end" className={classes.buttonRow}>
          <Grid item>
              <Button variant="contained" onClick={onClose} startIcon={<Close />}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSave}
                disabled={isSaveDisabled}
                startIcon={<Save />}
              >
                Save
              </Button>
            </Grid>
            
          </Grid>
        </Box>
      </Fade>
    </Modal>
  );
};

const useStyles = makeStyles<Theme>(() => ({
  form: {
    padding: theme.spacing(1),
    width: '100%',
    boxSizing: 'border-box',
  },
  buttonRow: {
    marginTop: theme.spacing(1),
  },
}));